import { toast } from 'react-toastify';

import { addLeaders, createWallet, getWallet } from 'api/copyBotApi';
import { getNotificationContent } from 'components';
import { SCAN_NAMES } from 'consts';
import { CT_CHAIN_ID } from 'consts/copyTrading';
import { useCopyBotAuth, useTransactionExpired } from 'hooks';
import { store } from 'store';
import { bn } from 'utils/BigNumber';
import { getErc20Abi, getScanUrl } from 'utils/helpers';
import { handleWeb3Error } from 'utils/helpers/handleError';
import { Address, TransactionReceipt } from 'viem';
import { usePublicClient, useWriteContract } from 'wagmi';

type WalletLike = {
  proxyWallet: Address;
};

export const useDeposit = () => {
  const { account } = store.web3Store;
  const { value, tokenIn, walletsToCopy, setValue, setWalletsToCopy, closeCopyModal } =
    store.copyFormStore;
  const { fetchPortfolio } = store.portfolioStore;
  const { getAccessToken, clearAccessToken } = useCopyBotAuth();

  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient({ chainId: CT_CHAIN_ID });
  const { setExpiredTimer, clearExpiredTimer } = useTransactionExpired();

  const getExistingWallet = async () => {
    if (!account) return null;

    return getWallet(account).catch((err: any) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });
  };

  const getOrCreateWallet = async (): Promise<WalletLike | null> => {
    if (!account) return null;

    const existingWallet = await getExistingWallet();
    if (existingWallet) return { proxyWallet: existingWallet.proxyWallet };

    const token = await getAccessToken(account);
    const createdWallet = await createWallet(
      {
        owner: account,
        enabled: true,
        leaders: walletsToCopy.map((address) => ({ address, enabled: true })),
      },
      token
    );

    return { proxyWallet: createdWallet.proxyWallet };
  };

  const syncLeadersIfNeeded = async (walletAlreadyExisted: boolean) => {
    if (!account || walletsToCopy.length === 0 || !walletAlreadyExisted) return;

    try {
      const token = await getAccessToken(account);
      await addLeaders(
        account,
        {
          leaders: walletsToCopy.map((address) => ({ address, enabled: true })),
        },
        token
      );
    } catch (err: any) {
      if (err?.response?.status === 401 && account) {
        clearAccessToken(account);
      }
      console.error('Failed to sync leaders after deposit:', err);
      toast.error('Deposit succeeded, but failed to update copied leaders');
    }
  };

  const deposit = async () => {
    if (!account || !tokenIn || !publicClient) return false;

    let isSuccess = false;

    try {
      const existingWallet = await getExistingWallet();
      const wallet = existingWallet || (await getOrCreateWallet());
      const proxyWallet = wallet?.proxyWallet;

      if (!proxyWallet) {
        toast.error('Proxy wallet is unavailable');
        return false;
      }

      const amount = BigInt(bn(value).toDecimals(tokenIn.decimals).toFixed(0));
      if (amount <= 0n) return false;

      const params = {
        account,
        abi: getErc20Abi(CT_CHAIN_ID, tokenIn.address),
        address: tokenIn.address,
        functionName: 'transfer',
        args: [proxyWallet, amount],
        chainId: CT_CHAIN_ID,
      } as const;

      setExpiredTimer();
      const hash = await writeContractAsync(params);
      const receipt: TransactionReceipt = await publicClient.waitForTransactionReceipt({ hash });
      const scanLink = getScanUrl(`/tx/${receipt.transactionHash}`, CT_CHAIN_ID);

      if (receipt.status === 'success') {
        await syncLeadersIfNeeded(!!existingWallet);

        toast.success(
          getNotificationContent({
            title: 'Deposit succeeded',
            href: scanLink,
            scanName: SCAN_NAMES[CT_CHAIN_ID],
          })
        );
        isSuccess = true;
        setWalletsToCopy([]);
        setValue('');
        closeCopyModal();
        fetchPortfolio(true);
      }

      if (receipt.status === 'reverted') {
        toast.error(
          getNotificationContent({
            isError: true,
            title: 'Failed to deposit',
            href: scanLink,
            scanName: SCAN_NAMES[CT_CHAIN_ID],
          })
        );
      }
    } catch (err: any) {
      if (err?.response?.status === 401 && account) {
        clearAccessToken(account);
      }
      handleWeb3Error({ err, source: 'deposit' });
    }

    clearExpiredTimer();
    return isSuccess;
  };

  return deposit;
};
